import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import api from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { Reveal, Overline } from "../components/site/Reveal";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import FocalImage from "../components/FocalImage";

const DEFAULT_ORDER = ["hero", "spotlights", "membership", "faq"];

export default function Community() {
  const { content, lang, t } = useLang();
  const c = content.community || {};
  const faq = content.faq || [];
  const [tiers, setTiers] = useState([]);
  const [members, setMembers] = useState([]);
  const [layout, setLayout] = useState(null);

  useEffect(() => {
    api.get("/membership-tiers").then((r) => setTiers(r.data)).catch(() => {});
    api.get("/members?spotlight=true").then((r) => setMembers(r.data)).catch(() => {});
    api.get("/page-config/community").then((r) => setLayout(r.data.sections))
      .catch(() => setLayout(DEFAULT_ORDER.map((id) => ({ id, enabled: true }))));
  }, []);

  const price = (p) => (p === 0 ? t("Free", "Gratuit") : `$${p}`);

  const blocks = {
    hero: (
      <section className="max-w-7xl mx-auto px-5 md:px-8 pt-10 md:pt-16 pb-16">
        <Reveal><Overline>{c.hero_overline}</Overline></Reveal>
        <Reveal delay={0.1}>
          <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-medium tracking-tight leading-[1.05] mt-5 max-w-4xl text-primary" data-testid="community-title">{c.hero_title}</h1>
        </Reveal>
        <Reveal delay={0.2}><p className="text-lg text-foreground/70 mt-6 max-w-2xl leading-relaxed">{c.hero_body}</p></Reveal>
      </section>
    ),
    spotlights: (
      <section className="max-w-7xl mx-auto px-5 md:px-8 pb-20">
        <Reveal><Overline>{c.spotlight_overline}</Overline></Reveal>
        <Reveal delay={0.1}><h2 className="font-display text-4xl md:text-5xl font-medium mt-4 text-primary">{c.spotlight_title}</h2></Reveal>
        <div className="grid md:grid-cols-3 gap-6 mt-10">
          {members.map((m, i) => (
            <Reveal key={m.id} delay={i * 0.08}>
              <div className="bg-card border border-primary/10 rounded-3xl overflow-hidden group h-full">
                <div className="aspect-[4/3] overflow-hidden">
                  <div className="w-full h-full group-hover:scale-105 transition-transform duration-500"><FocalImage img={{ url: m.image, ...(m.image_focal || {}) }} alt={m.name} /></div>
                </div>
                <div className="p-6">
                  <p className="font-display text-2xl font-semibold text-primary">{m.name}</p>
                  <p className="text-sm text-secondary font-medium">{lang === "fr" ? m.role_fr : m.role_en}</p>
                  <p className="text-foreground/70 mt-3 leading-relaxed italic">"{lang === "fr" ? m.quote_fr : m.quote_en}"</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>
    ),
    membership: (
      <section className="bg-card/60 py-20 md:py-28" data-testid="membership-section">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <Reveal><Overline>{c.membership_overline}</Overline></Reveal>
          <Reveal delay={0.1}><h2 className="font-display text-4xl md:text-5xl font-medium mt-4 text-primary">{c.membership_title}</h2></Reveal>
          <div className="grid md:grid-cols-3 gap-6 mt-12 items-start">
            {tiers.map((tier, i) => (
              <Reveal key={tier.id} delay={i * 0.1}>
                <div className={`rounded-3xl p-8 h-full border ${tier.popular ? "bg-primary text-primary-foreground border-primary shadow-2xl shadow-primary/20 md:-translate-y-4" : "bg-background border-primary/10"}`}>
                  {tier.popular && <span className="inline-block text-xs uppercase tracking-widest font-semibold bg-secondary text-secondary-foreground rounded-full px-3 py-1 mb-4">{t("Most popular", "Le plus populaire")}</span>}
                  <h3 className="font-display text-3xl font-semibold">{lang === "fr" ? tier.name_fr : tier.name_en}</h3>
                  <p className="mt-2"><span className="font-display text-5xl font-semibold">{price(tier.price)}</span>{tier.price > 0 && <span className={tier.popular ? "text-primary-foreground/60" : "text-muted-foreground"}>/{t("mo", "mois")}</span>}</p>
                  <ul className="mt-6 space-y-3">
                    {(lang === "fr" ? tier.benefits_fr : tier.benefits_en).map((b, j) => (
                      <li key={j} className="flex gap-3 items-start">
                        <Check size={18} className={tier.popular ? "text-secondary mt-0.5 shrink-0" : "text-secondary mt-0.5 shrink-0"} />
                        <span className={tier.popular ? "text-primary-foreground/90" : "text-foreground/80"}>{b}</span>
                      </li>
                    ))}
                  </ul>
                  <Link to={`/join?tier=${tier.key}`} data-testid={`tier-cta-${tier.key}`} className={`block text-center mt-8 rounded-full px-6 py-3 font-semibold transition-colors ${tier.popular ? "bg-secondary text-secondary-foreground hover:opacity-90" : "bg-primary text-primary-foreground hover:bg-secondary"}`}>
                    {lang === "fr" ? tier.cta_fr : tier.cta_en}
                  </Link>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    ),
    faq: (
      <section className="max-w-3xl mx-auto px-5 md:px-8 py-20 md:py-28">
        <Reveal><Overline>{c.faq_overline}</Overline></Reveal>
        <Reveal delay={0.1}><h2 className="font-display text-4xl md:text-5xl font-medium mt-4 text-primary mb-8">{c.faq_title}</h2></Reveal>
        <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
          {faq.map((f, i) => (
            <AccordionItem key={i} value={`item-${i}`} className="border-primary/10">
              <AccordionTrigger className="text-left font-display text-xl text-primary hover:text-secondary">{f.q}</AccordionTrigger>
              <AccordionContent className="text-foreground/70 leading-relaxed text-base">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>
    ),
  };

  const order = layout || DEFAULT_ORDER.map((id) => ({ id, enabled: true }));

  return (
    <div data-testid="community-page">
      {order.filter((s) => s.enabled && blocks[s.id]).map((s) => (
        <React.Fragment key={s.id}>{blocks[s.id]}</React.Fragment>
      ))}
    </div>
  );
}
